import React, { useState, useMemo } from 'react';
import {
    Table,
    Header,
    Button,
    Pagination,
    PropertyFilter,
    SpaceBetween,
} from '@amzn/awsui-components-react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { CloudWatchRecord, TablePreferences } from './types';
import { EmptyState } from '../../../../utils/tablePreferences';
import {
    generateColumns,
    generateFilteringProperties,
    getFilterCounterText,
    PAGE_SIZE_OPTIONS
} from './constants';
import { Preferences } from './Preferences';
import { PROPERTY_FILTERING_I18N_CONSTANTS } from '../../../../utils/constants';
import { ExportCSV } from "../../../../utils/exportCSV";

export const LogsView: React.FC<{ data: CloudWatchRecord[] }> = ({ data }) => {
    const [preferences, setPreferences] = useState<TablePreferences>(() => ({
        pageSize: 10,
        visibleContent: generateColumns(data).map(col => col.id),
        wrapLines: true,
    }));

    const columns = useMemo(() => generateColumns(data), [data]);
    const filteringProperties = useMemo(() => generateFilteringProperties(data), [data]);

    const { items, collectionProps, propertyFilterProps, paginationProps, actions, filteredItemsCount } = useCollection(data, {
        pagination: { pageSize: preferences.pageSize },
        sorting: { defaultState: { sortingColumn: { sortingField: 'timestamp' } } },
        propertyFiltering: {
            filteringProperties,
            empty: (
                <EmptyState
                    title="No records"
                    subtitle="No records to display."
                    action={<span></span>}
                />
            ),
            noMatch: (
                <EmptyState
                    title="No matches"
                    subtitle="We can’t find a match."
                    action={
                        <Button onClick={() => actions.setFiltering('')}>
                            Clear filter
                        </Button>
                    }
                />
            ),
        },
    });

    const visibleContentOptions = useMemo(() => [{
        label: 'Main properties',
        options: columns.map(col => ({ id: col.id, label: col.header })),
    }], [columns]);

    return (
        <Table
            {...collectionProps}
            header={
                <Header
                    counter={`(${items.length} of ${filteredItemsCount})`}
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <PropertyFilter
                                {...propertyFilterProps}
                                filteringProperties={filteringProperties}
                                countText={getFilterCounterText(filteredItemsCount)}
                                i18nStrings={PROPERTY_FILTERING_I18N_CONSTANTS}
                            />
                            <ExportCSV
                                data={data}
                                filename="Logs"
                                loading={false}
                            />
                        </SpaceBetween>
                    }
                >
                   Logs
                </Header>
            }
            columnDefinitions={columns}
            items={items}
            pagination={<Pagination {...paginationProps} />}
            preferences={
                <Preferences
                    preferences={preferences}
                    setPreferences={setPreferences}
                    disabled={false}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    visibleContentOptions={visibleContentOptions}
                />
            }
            stickyHeader
            resizableColumns
            wrapLines={preferences.wrapLines}
            visibleColumns={preferences.visibleContent}
        />
    );
};